/* Read-only adapter for Zepto's observed pricing integration.
 * The documented app-ready event is not a price-commit event. Observe the
 * existing pplrqty call AFTER it returns and verify its DOM write; never call
 * it ourselves, change its return value, or calculate a replacement total.
 * Unknown pricing modes/currency conversions fail closed to the original UI.
 */
(() => {
  'use strict';
  if (customElements.get('bw-name-price')) return;
  const instances = new Set();
  let hook;
  let compatible = false;
  const textSelector = '.pplr-text input.pplr_text:not([type="hidden"]),.pplr-text textarea.pplr_text';
  const positive = value => Number.isFinite(Number(value)) && Number(value) > 0;
  const active = field => !field.disabled && !field.closest('.pplr_no_preview') &&
    (!['checkbox', 'radio'].includes(field.type) || field.checked) &&
    field.value?.trim() && field.value !== 'blank';
  const currentPrice = price => price?.querySelector(price.classList.contains('price--on-sale') ?
    '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
  const elementOf = node => node?.nodeType === 1 ? node : node?.parentElement;

  function bindPricing() {
    if (!instances.size || typeof window.pplrqty !== 'function') return;
    if (hook?.wrapped === window.pplrqty) return;
    // Check the observed pricing capabilities, not the CDN release number.
    // Minor Zepto releases can rename local variables without changing this
    // contract. A later DOM-write/return-value check still gates each label.
    const source = Function.prototype.toString.call(window.pplrqty);
    compatible = ['_pplr_price_cache', 'data-pplr_price', 'pplr_product.variants',
      'pplrFontPickerPricedElsewhere', 'jQuery.trim', 'convertAndRound'].every(marker => source.includes(marker));
    if (!compatible) return;
    const original = window.pplrqty;
    const wrapped = function () {
      const matching = [...instances].filter(instance =>
        String(window.pplr_product?.id) === instance.dataset.productId);
      matching.forEach(instance => { try { instance.prepare(); } catch (_) { instance.reset(); } });
      const result = original.apply(this, arguments);
      // Display failures must never interrupt Zepto or its add-to-cart path.
      matching.forEach(instance => { try { instance.committed(result); } catch (_) { instance.reset(); } });
      return result;
    };
    hook = { original, wrapped };
    window.pplrqty = wrapped;
  }
  function ready() {
    bindPricing();
    instances.forEach(instance => instance.schedule());
  }
  function scriptLoaded(event) {
    if (event.target.tagName === 'SCRIPT' && /product-personalizer\//.test(event.target.src)) ready();
  }
  function listen(add) {
    const method = add ? 'addEventListener' : 'removeEventListener';
    window[method]('pplrAppInitiated', ready);
    window[method]('pplrAppBuild', ready); // Also dispatched by the inspected vendor build.
    window[method]('pageshow', ready);
    document[method]('load', scriptLoaded, true);
  }

  class NamePrice extends HTMLElement {
    connectedCallback() {
      if (this.root) return;
      this.root = this.closest('product-info[id^="MainProduct-"]');
      if (!this.root) return;
      this.captions = new Map();
      this.notes = new Set();
      this.pending = false;
      this.onInput = () => { bindPricing(); this.schedule(); };
      this.onUnload = event => { if (event.target.contains(this)) this.dispose(); };
      this.priceObserver = new MutationObserver(() => this.schedule());
      this.appObserver = new MutationObserver(() => this.schedule());
      this.stickyObserver = new MutationObserver(records => {
        if (records.some(record => elementOf(record.target)?.closest('.price__container') ||
          [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 &&
            (node.matches('.price,.price__container') || node.querySelector('.price,.price__container'))))) this.schedule();
      });
      // Structural changes only: reconnect narrow observers when Shopify/app
      // replaces the price, personalizer or sticky-bar markup. Ignore canvases,
      // counters, captions and our annotation nodes.
      this.structureObserver = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
          node.nodeType === 1 && (node.matches('.product-personalizer,[id^="price-"],.sticky-atc') ||
            node.querySelector('.product-personalizer,[id^="price-"],.sticky-atc'))))) this.schedule();
      });
      this.structureObserver.observe(this.root, { childList: true, subtree: true });
      this.root.addEventListener('input', this.onInput);
      this.root.addEventListener('change', this.onInput);
      document.addEventListener('shopify:section:unload', this.onUnload);
      if (!instances.size) listen(true);
      instances.add(this);
      bindPricing();
      this.sync();
    }
    disconnectedCallback() { this.dispose(); }
    reset() { this.state = null; this.schedule(); }
    schedule() {
      if (this.pending || !this.root) return;
      this.pending = true;
      queueMicrotask(() => { this.pending = false; if (this.root) this.sync(); });
    }
    refresh() {
      const main = this.root.querySelector(`[id="price-${this.dataset.sectionId}"] .price`);
      const app = this.root.querySelector(`.product-personalizer[id="pplr-${this.dataset.productId}"]`);
      const bar = this.root.querySelector('.sticky-atc');
      if (main !== this.main) {
        this.priceObserver.disconnect();
        this.main = main;
        this.state = null;
        if (main) this.priceObserver.observe(main, { childList: true, subtree: true, characterData: true,
          attributes: true, attributeFilter: ['class'] });
      }
      if (app !== this.app) {
        this.appObserver.disconnect();
        this.app = app;
        this.state = null;
        if (app) this.appObserver.observe(app, { childList: true, subtree: true, attributes: true,
          attributeFilter: ['class', 'disabled', 'data-pplr_price', 'data-pplr_price-type', 'data-pplr_f_price'] });
      }
      if (bar !== this.bar) {
        this.stickyObserver.disconnect();
        this.bar = bar;
        if (bar) this.stickyObserver.observe(bar, { childList: true, subtree: true, characterData: true });
      }
    }
    fields() {
      // Observed type 2 is a fixed paid text option. Other modes keep Zepto's UI.
      if (!compatible || !this.app || window.pplr_modify_price || window.pplr_addi_price ||
        this.dataset.currency !== this.dataset.shopCurrency ||
        (window.Shopify?.currency?.active && window.Shopify.currency.active !== this.dataset.currency)) return [];
      return [...this.app.querySelectorAll(textSelector)].filter(field =>
        field.matches('.pplraddprice,.pplr_f_addprice') && !field.getAttribute('data-variant') &&
        field.getAttribute('data-pplr_price-type') === '2' && positive(field.getAttribute('data-pplr_price')));
    }
    signature(fields) {
      return JSON.stringify(fields.map(field => [field.name, field.dataset.frame, !!active(field),
        field.getAttribute('data-pplr_price'), field.getAttribute('data-pplr_f_price')]));
    }
    variant() {
      const form = this.app?.closest('form');
      return (form?.querySelector('[name="id"]:not([disabled])') ||
        this.root.querySelector('[name="id"]:not([disabled])'))?.value || '';
    }
    prepare() { this.refresh(); this.priceObserver.takeRecords(); }
    committed(result) {
      const node = currentPrice(this.main);
      const writes = this.priceObserver.takeRecords();
      const targets = Array.from(window._pplr_price_cache || []);
      const fields = this.fields();
      const others = [...this.app?.querySelectorAll('.pplraddprice,.pplr_f_addprice') || []].filter(field =>
        active(field) && !fields.includes(field) && (positive(field.getAttribute('data-pplr_price')) ||
          positive(field.getAttribute('data-pplr_f_price'))));
      const wrotePrice = node && writes.some(record => {
        const target = elementOf(record.target);
        return target === node || node.contains(target);
      });
      const ownsPrice = node && targets.some(target => target === node || node.contains(target) || target.contains(node));
      const appMatches = String(window.pplr_product?.id) === this.dataset.productId;
      // A non-empty input alone is never evidence. Require Zepto's own positive
      // returned surcharge and a synchronous write to THIS displayed price.
      this.state = appMatches && fields.length && !others.length && wrotePrice && ownsPrice &&
        Number.isFinite(result) && result > 0 && fields.some(active) ? {
          node, text: node.textContent, signature: this.signature(fields), variant: this.variant(),
          activeFields: new Set(fields.filter(active))
        } : null;
      this.schedule();
    }
    note(price, show) {
      let note = price.querySelector(':scope > .bw-name-price-note');
      if (show && !note) {
        note = document.createElement('span');
        note.className = 'bw-name-price-note';
        note.textContent = 'inclusief naam';
        price.appendChild(note);
        this.notes.add(note);
      }
      if (note) note.hidden = !show;
    }
    sync() {
      this.refresh();
      const fields = this.fields();
      const node = currentPrice(this.main);
      const state = this.state;
      const confirmed = !!(state && state.node === node && state.text === node?.textContent &&
        state.signature === this.signature(fields) && state.variant === this.variant());
      if (!confirmed) this.state = null;
      const labels = new Set();
      fields.forEach(field => {
        const label = field.closest('.pplr-text')?.querySelector(':scope > .pplrlabel');
        if (!label) return;
        labels.add(label);
        if (!this.captions.has(label)) this.captions.set(label, label.getAttribute('data-bw-name-caption'));
        const amount = new Intl.NumberFormat(this.dataset.locale || 'nl', {
          style: 'currency', currency: this.dataset.currency
        }).format(Number(field.getAttribute('data-pplr_price')));
        const emptyCaption = `Maak het extra persoonlijk met een naam of korte tekst — + ${amount}`;
        const caption = confirmed && state.activeFields.has(field) ? 'Welke naam mag erop?' : emptyCaption;
        // Let the current caption wrap naturally. Reserving the longer empty
        // caption's height leaves a blank line after the confirmed short label.
        if (label.getAttribute('data-bw-name-caption') !== caption) label.setAttribute('data-bw-name-caption', caption);
      });
      this.captions.forEach((original, label) => {
        if (!labels.has(label)) { this.restoreCaption(label, original); this.captions.delete(label); }
      });
      if (this.main) this.note(this.main, confirmed);
      this.bar?.querySelectorAll('.sticky-atc-price .price').forEach(price => {
        // Preserve quantity-break behavior; annotate only an exact mirror of
        // the committed main price, never an unrelated/old sticky total.
        this.note(price, confirmed && currentPrice(price)?.textContent === node.textContent);
      });
    }
    restoreCaption(label, original) {
      if (original === null) label.removeAttribute('data-bw-name-caption');
      else label.setAttribute('data-bw-name-caption', original);
    }
    dispose() {
      if (!this.root) return;
      [this.priceObserver, this.appObserver, this.stickyObserver, this.structureObserver].forEach(observer => observer.disconnect());
      this.captions.forEach((original, label) => this.restoreCaption(label, original));
      this.notes.forEach(note => note.remove());
      this.root.removeEventListener('input', this.onInput);
      this.root.removeEventListener('change', this.onInput);
      document.removeEventListener('shopify:section:unload', this.onUnload);
      instances.delete(this);
      if (!instances.size) {
        listen(false);
        if (hook && window.pplrqty === hook.wrapped) window.pplrqty = hook.original;
        hook = null;
        compatible = false;
      }
      this.state = this.root = null;
    }
  }
  customElements.define('bw-name-price', NamePrice);
})();
